import React from 'react'
import { X } from 'lucide-react'
import { mergeClasses } from '@/helpers/styling'
import { useFloatingState } from './panel-registry'
import { SvgIcon, SvgIconType } from '@/components/svg-icon'
import { SimpleTooltip } from '@/components/ui-primitives/simple-tooltip'
import { ResizeHandle } from '@/icons/resize-handle'
import { createPortalLayer, PortalHiddenAnchor } from "@/shared/portal-layers"

export const PanelChromeRoot = createPortalLayer(function PanelChrome(props: {
  children: React.ReactNode
}) {
  const floatingState = useFloatingState()
  return (
    <div
      tabIndex={-1}
      data-portal-layer
      className={mergeClasses(`
           w-full h-full min-h-0 flex flex-col overflow-hidden
           position-relative 
           text-xs font-inter`, {
        "border-l border-border bg-white0": floatingState === "docked",
        "rounded border border-transparent p-[1px]": floatingState !== "docked",
        "shadow bg-white focus:!border-wistor": floatingState === "floating-active",
        "p-[1px] bg-white": false
      })}>
      <PortalHiddenAnchor />
      {props.children}
      {(floatingState !== "docked") && (
        <ResizeHandle
          size={14}
          className="w-5 h-5 position-absolute z-1 bottom-0 right-0 text-neutral-400" />
      )}
    </div>
  )
})

function PanelChromeBody(props: {
  children: React.ReactNode
}) {
  const floatingState = useFloatingState()
  return (
    <div className={mergeClasses("flex flex-col flex-grow min-h-0 h-full w-full bg-white0 overflow-auto", {
      "rounded-bl rounded-br": floatingState !== "docked"
    })}>
      {props.children}
    </div>
  )
}

function PanelChromeHeader(props: {
  title: string
  onClose: () => void
  showClose?: boolean
  icon?: SvgIconType
  extraIcons?: React.ReactNode
  children: React.ReactNode
}) {
  const floatingState = useFloatingState()
  return (
    <div className={mergeClasses("flex items-center gap-1.5 bg-white0 border-b border-border text-foreground/80 select-none whitespace-nowrap overflow-hidden transition-colors duration-200", {
      "text-white rounded-tl rounded-tr": floatingState !== "docked",
      "border-b border-gray-300": floatingState === "docked" && false,
      "bg-wistor": floatingState === "floating-active",
      "bg-[#3e3474]": floatingState === "floating-inactive",
    })}>
      <div className={mergeClasses("flex-grow flex items-center font-exo text-[13px] font-semibold p-1.5 pl-2.5 gap-2", {
        "drag-handle cursor-move": floatingState !== "docked"
      })}>
        {props.icon && <SvgIcon icon={props.icon} size={14} className="xmt-[1px]"  />}
        <span className="inline-flex pb-[1px]">
          {props.children}
        </span>
      </div>
      <div className="flex flex-shrink gap-0.5 p-1.5 items-center group">
        {props.extraIcons}
        {props.showClose !== false && (
          <PanelChrome.HeaderIcon title="Close" icon={X} onClick={props.onClose} />
        )}
      </div>
    </div>
  )
}

function PanelChromeHeaderIcon(props: {
  title: string
  icon: SvgIconType
  onClick: () => void
  disabled?: boolean
  iconSize?: number
  className?: string
  svgAttributes?: React.SVGAttributes<SVGElement>
}) {
  const floatingState = useFloatingState()
  return (
    <SimpleTooltip tooltip={props.disabled ? null : props.title}>
      <button type="button"
              onClick={props.onClick}
              disabled={props.disabled}
              className={mergeClasses("rounded-md p-1 opacity-75 transition", {
                "hover:opacity-100": !props.disabled,
                "hover:bg-white hover:bg-opacity-25": floatingState !== "docked" && !props.disabled,
                "hover:bg-muted": floatingState === "docked" && !props.disabled,
                "opacity-50": props.disabled
              }, props.className)}>
        <SvgIcon
          icon={props.icon}
          size={props.iconSize ?? 14}
          {...props.svgAttributes} />
      </button>
    </SimpleTooltip>
  )
}

export const PanelChrome = Object.assign(PanelChromeRoot, {
  Body: PanelChromeBody,
  Header: PanelChromeHeader,
  HeaderIcon: PanelChromeHeaderIcon,
})
